import type { ElementType } from "react"

import { Heading, Text } from "@/components/ui"
import { TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"

interface MosaicTile {
  id: string
  title: string
  category?: string
  image: string
  href?: string
  serveId?: string
}

// The mosaic is the page's one square-cornered object. Everything around it is a
// rounded card on the warm-grey field, so cutting these four to the edge is what
// makes it read as a stop rather than another row of tiles — see the two rounding
// registers in the reference.
const SPANS = ["@xl:col-span-7", "@xl:col-span-5", "@xl:col-span-5", "@xl:col-span-7"]

export const Mosaic = ({ items }: { items: MosaicTile[] }) => (
  <div className="@container">
    <div className="grid grid-cols-12 gap-50">
      {items.map((item, index) => {
        const Tile: ElementType = item.href ? Link : "div"
        return (
          <Tile
            key={item.id}
            {...(item.href ? { href: item.href } : {})}
            data-nelo-serve={item.serveId}
            className={`group bg-surface-muted aspect-mosaic focus-visible:ring-brand relative col-span-6 block overflow-hidden focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset @xl:aspect-auto @xl:min-h-60 @3xl:min-h-90 ${SPANS[index % SPANS.length]}`}
          >
            <TileImage
              src={item.image}
              alt={item.title}
              fill
              sizes="(max-width: 576px) 50vw, 60vw"
              className="duration-slow ease-productive object-cover transition-transform motion-safe:group-hover:scale-105"
            />
            {/* The label is inset, so it can't carry its own ground — this band
                does, and it's tall enough that a two-line title stays on it. */}
            <span className="bg-scrim-label absolute inset-x-0 bottom-0 h-1/2" aria-hidden />
            <span className="duration-moderate ease-productive absolute inset-x-250 bottom-250 transition-transform motion-safe:group-hover:-translate-y-100 @xl:inset-x-500 @xl:bottom-500">
              <Heading as="span" variant="sm" family="display" tone="inverted" balance className="@xl:text-heading-lg @3xl:text-heading-xl block font-bold tracking-tight">
                {item.title}
              </Heading>
              {item.category && (
                <Text as="span" variant="eyebrow" tone="inherit" className="text-on-scrim mt-150 block">
                  {item.category}
                </Text>
              )}
            </span>
          </Tile>
        )
      })}
    </div>
  </div>
)
